import { MenuItem, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { useUserStore } from '@/configs/store';
import { trackerUrls } from '@/tracker/config/urls';
import { formatEstimation } from '@/tracker/entities/worklog';
import { Task } from '@/tracker/types';
import { formatDateTime } from '@/tracker/utils/formatDate';

import { CardField } from '../CardField';
import { IssueBoardsFields } from '../IssueBoardsFields';
import { StatusSelect } from '../StatusSelect';
import { TaskPicker } from '../TaskPicker';
import { TextEditor } from '../TextEditor';
import { UserPicker } from '../UserPicker';
import { WorklogSection } from '../WorklogSection';
import { IssueCardStore } from '../../model/IssueCardStore';
import { useIssueDrawer } from '../../useIssueDrawer';

import { toEstimation, validateEstimation } from './estimation';

import s from './IssueCardAside.module.scss';

type Props = {
  issue: Task;
  store: IssueCardStore;
};

type EstimationField = 'originalEstimation' | 'estimation';

/** Правая панель карточки: поля задачи, учёт времени, доски и спринты */
const IssueCardAside: React.FC<Props> = observer(({ issue, store }) => {
  const { user, isAdmin } = useUserStore();
  const { issueLink } = useIssueDrawer();
  const isLocked = store.isSaving || store.isReadOnly;

  const renderEstimationEditor = (field: EstimationField, label: string) => (close: () => void) => (
    <TextEditor
      initialValue={issue[field] === null ? '' : formatEstimation(issue[field])}
      ariaLabel={label}
      placeholder="Например, 2ч 30м"
      validate={validateEstimation}
      onSave={(text) => store.update({ [field]: toEstimation(text) })}
      onClose={close}
    />
  );

  return (
    <aside className={s.aside}>
      <CardField label="Статус">
        <StatusSelect
          status={issue.status}
          statuses={store.queueStatuses}
          disabled={isLocked}
          onChange={store.changeStatus}
        />
      </CardField>
      <CardField
        label="Исполнитель"
        disabled={isLocked}
        editor={(close) => (
          <UserPicker
            ariaLabel="Исполнитель"
            emptyLabel="Не назначен"
            onSave={(assignee) => store.update({ assignee })}
            onClose={close}
          />
        )}
      >
        {issue.assignee?.displayName ?? '—'}
      </CardField>
      <CardField
        label="Тип"
        disabled={isLocked || store.taskTypes.length === 0}
        editor={(close) => (
          <TextField
            select
            fullWidth
            size="small"
            value={issue.type.key}
            onChange={(event) => {
              close();

              if (event.target.value !== issue.type.key) {
                store.update({ type: event.target.value });
              }
            }}
            slotProps={{
              select: { defaultOpen: true, onClose: close },
              htmlInput: { 'aria-label': 'Тип задачи' },
            }}
          >
            {store.taskTypes.map((type) => (
              <MenuItem key={type.key} value={type.key}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      >
        {issue.type.name}
      </CardField>
      <CardField
        label="Родительская задача"
        disabled={isLocked}
        editor={(close) => (
          <TaskPicker
            search={store.searchTasks}
            ariaLabel="Родительская задача"
            noneLabel={issue.parent ? 'Без родителя' : undefined}
            onSave={(parent) => store.update({ parent })}
            onClose={close}
          />
        )}
      >
        {issue.parent ? (
          <>
            <Link to={issueLink(issue.parent.key)} className={s.aside__link}>
              {issue.parent.key}
            </Link>{' '}
            {issue.parent.summary}
          </>
        ) : (
          '—'
        )}
      </CardField>
      <CardField
        label="Оценка"
        disabled={isLocked}
        editor={renderEstimationEditor('originalEstimation', 'Оценка')}
      >
        {formatEstimation(issue.originalEstimation)}
      </CardField>
      <CardField
        label="Осталось"
        disabled={isLocked}
        editor={renderEstimationEditor('estimation', 'Осталось')}
      >
        {formatEstimation(issue.estimation)}
      </CardField>
      <CardField label="Затрачено">
        {formatEstimation(issue.spent)}
        <WorklogSection store={store} currentUserId={user?.id ?? null} isAdmin={isAdmin} />
      </CardField>
      <CardField label="Очередь">
        <Link to={trackerUrls.queue(issue.queue.key)} className={s.aside__link}>
          {issue.queue.name}
        </Link>
      </CardField>
      <IssueBoardsFields issue={issue} store={store} />
      <CardField label="Автор">{issue.createdBy?.displayName ?? '—'}</CardField>
      <CardField label="Создана">{formatDateTime(issue.createTime)}</CardField>
      <CardField label="Обновлена">{formatDateTime(issue.updateTime)}</CardField>
    </aside>
  );
});

export { IssueCardAside };
